<template>
  <div v-if="showModule" class="doc-by-category-module">
    <Toast position="bottom-center" />

    <div v-if="loading" class="doc-by-category-state">
      <div class="doc-by-category-surface">
        <div class="doc-by-category-skeleton-row" v-for="index in 4" :key="index">
          <Skeleton width="2.75rem" height="2.75rem" borderRadius="14px" />
          <div class="doc-by-category-skeleton-copy">
            <Skeleton width="12rem" height="0.95rem" borderRadius="999px" />
            <Skeleton width="8rem" height="0.85rem" borderRadius="999px" />
          </div>
          <Skeleton width="1.25rem" height="1.25rem" borderRadius="999px" />
        </div>
      </div>
    </div>

    <div v-else-if="error" class="doc-by-category-state">
      <div class="doc-by-category-surface doc-by-category-surface--centered">
        <EmptyState
          :title="t('wkt.client.documents.error-state.title')"
          :description="t('wkt.client.documents.error-state.description')"
        />
        <Button :label="t('wkt.client.documents.actions.retry')" @click="loadCategoryDocuments" />
      </div>
    </div>

    <div v-else class="doc-by-category-content">
      <div class="doc-by-category-surface">
        <div v-if="filteredDocuments.length" class="doc-by-category-list">
          <button
            v-for="document in filteredDocuments"
            :key="document.id"
            type="button"
            class="doc-by-category-row"
            @click="openDocument(document)"
          >
            <i :class="['doc-by-category-icon', documentIconClass(document.documentType)]" aria-hidden="true"></i>

            <div class="doc-by-category-copy">
              <div class="doc-by-category-name-row">
                <span class="doc-by-category-name">{{ documentDisplayName(document) }}</span>
                <span v-if="isPersonalDocument(document)" class="doc-by-category-pill">
                  {{ t("wkt.client.documents.doc-by-category.personal_document") }}
                </span>
                <span v-if="isUnreadDocument(document)" class="doc-by-category-unread">
                  {{ t("wkt.client.documents.doc-by-category.unread") }}
                </span>
              </div>
              <span v-if="formatDateTime(document.creationDate)" class="doc-by-category-date">
                {{ formatDateTime(document.creationDate) }}
              </span>
            </div>

            <i
              :class="['doc-by-category-action', isFormSubmission(document) ? 'pi pi-external-link' : 'pi pi-download']"
              aria-hidden="true"
            ></i>
          </button>
        </div>

        <div v-else class="doc-by-category-empty">
          <EmptyState
            :title="t('wkt.client.documents.doc-by-category.category-empty-state.title')"
            :description="t('wkt.client.documents.doc-by-category.category-empty-state.description')"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import documentsApi from "@wkt/api/documents/documentsAPI";
import type {
  DocByCategoryModulePayload,
  DocumentCategoryDTO,
  FullDocumentDTO,
} from "@wkt/api/documents";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useAlert } from "@wkt/composables/useAlert";

const DOCUMENT_TYPE_FILE = 0;
const DOCUMENT_TYPE_URL = 1;
const DOCUMENT_TYPE_REQUEST = 2;
const DOCUMENT_TYPE_SIGNATURE = 3;
const DOCUMENT_TYPE_PROCEDURE = 4;
const DOCUMENT_TYPE_FORM = 5;
const DOCUMENT_CLASS_FORM_SUBMISSION = "FORM_SUBMISSION";
const REQUEST_STATUS_PENDING = 1;

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const documents = ref<FullDocumentDTO[]>([]);
const currentCategory = ref<DocumentCategoryDTO | null>(null);
const downloadingDocumentId = ref<number | null>(null);

const { t } = useI18n();
const { showAlert } = useAlert();

function isPayload(value: unknown): value is DocByCategoryModulePayload {
  return Boolean(value) && typeof value === "object";
}

function parseCurrentCategoryFromHash(): DocumentCategoryDTO | null {
  const hash = window.location.hash || "";
  const [routeToken, ...segments] = hash.replace(/^#/, "").split(";");

  if (routeToken !== "docbycategory") {
    return null;
  }

  const params = new Map<string, string>();
  segments.forEach((segment) => {
    const [key, ...valueParts] = segment.split("=");
    if (!key) {
      return;
    }
    params.set(key, decodeURIComponent(valueParts.join("=") || ""));
  });

  const rawCategoryId = params.get("categoryId");
  const categoryName = params.get("categoryName") || "";
  if (rawCategoryId == null) {
    return null;
  }

  const categoryId = Number.parseInt(rawCategoryId, 10);
  if (Number.isNaN(categoryId)) {
    return null;
  }

  return {
    id: categoryId,
    name: categoryName,
  };
}

function isPendingRequestDocument(document: FullDocumentDTO): boolean {
  return document.documentPath == null && (
    document.documentType === DOCUMENT_TYPE_REQUEST ||
    (document.documentType === DOCUMENT_TYPE_SIGNATURE &&
      document.requestStatus === REQUEST_STATUS_PENDING)
  );
}

function isVisibleDocument(document: FullDocumentDTO): boolean {
  if (isPendingRequestDocument(document)) {
    return false;
  }

  return Boolean(document.showDocument);
}

function belongsToCurrentCategory(document: FullDocumentDTO): boolean {
  const categoryId = currentCategory.value?.id;
  if (categoryId == null) {
    return false;
  }

  if (categoryId === 0) {
    return document.documentCategoryId == null;
  }

  return document.documentCategoryId === categoryId;
}

const filteredDocuments = computed(() =>
  documents.value.filter((document) => isVisibleDocument(document) && belongsToCurrentCategory(document))
);

function applyPayload(payload?: unknown): void {
  const payloadCategory = isPayload(payload) ? payload.currentCategory : null;
  currentCategory.value = payloadCategory || parseCurrentCategoryFromHash();

  if (isPayload(payload) && Array.isArray(payload.documents)) {
    documents.value = payload.documents;
    loading.value = false;
    error.value = false;
    return;
  }

  loadCategoryDocuments();
}

function loadCategoryDocuments(): void {
  currentCategory.value = currentCategory.value || parseCurrentCategoryFromHash();
  loading.value = true;
  error.value = false;

  documentsApi.getDocumentsInfo(
    (response) => {
      documents.value = Array.isArray(response?.documents) ? response.documents : [];
      loading.value = false;
      error.value = false;
    },
    () => {
      loading.value = false;
      error.value = true;
    }
  );
}

function formatDateTime(value?: string | null): string {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function documentDisplayName(document: FullDocumentDTO): string {
  return (
    document.documentName?.trim() ||
    document.formName?.trim() ||
    document.templateName?.trim() ||
    t("wkt.client.documents.view_document")
  );
}

function documentIconClass(documentType?: number | null): string {
  switch (documentType) {
    case DOCUMENT_TYPE_FILE:
      return "pi pi-file";
    case DOCUMENT_TYPE_URL:
      return "pi pi-link";
    case DOCUMENT_TYPE_REQUEST:
      return "pi pi-upload";
    case DOCUMENT_TYPE_SIGNATURE:
      return "pi pi-pencil";
    case DOCUMENT_TYPE_PROCEDURE:
      return "pi pi-briefcase";
    case DOCUMENT_TYPE_FORM:
      return "pi pi-file-edit";
    default:
      return "pi pi-file";
  }
}

function isPersonalDocument(document: FullDocumentDTO): boolean {
  return Boolean(document.employeeId);
}

function isUnreadDocument(document: FullDocumentDTO): boolean {
  return Boolean(document.employeeId && !document.documentShowDate);
}

function isFormSubmission(document: FullDocumentDTO): boolean {
  return document.documentClass === DOCUMENT_CLASS_FORM_SUBMISSION;
}

function getClientCompanyId(): string {
  return (
    window.localStorage.getItem("worktime.client.company.id") ||
    String(gwtApi.getSessionInformation()?.company?.id || "")
  );
}

function openDocument(document: FullDocumentDTO): void {
  if (downloadingDocumentId.value != null) {
    return;
  }

  if (isFormSubmission(document)) {
    window.location.hash = `#form;formId=${encodeURIComponent(String(document.id))}`;
    return;
  }

  downloadingDocumentId.value = document.id;
  documentsApi.getDocumentById(
    document.id,
    () => {
      downloadingDocumentId.value = null;
      gwtApi.downloadFile(
        `user/getDocument/${getClientCompanyId()}/${document.id}`,
        documentDisplayName(document)
      );
    },
    () => {
      downloadingDocumentId.value = null;
      showAlert({
        text: t("wkt.client.documents.doc-by-category.item.not.available"),
        color: "error",
        timeout: 6000,
      });
      loadCategoryDocuments();
    }
  );
}

function onReveal(payload?: unknown): void {
  showModule.value = true;
  applyPayload(payload);
}

function refreshModule(): void {
  loadCategoryDocuments();
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.doc-by-category-module {
  --documents-surface: #ffffff;
  --documents-border: rgba(203, 213, 225, 0.7);
  --documents-subtle: #64748b;
  --documents-text: #0f172a;
  --documents-hover: rgba(24, 94, 169, 0.06);
  min-height: 100%;
  background: #ffffff;
}

.doc-by-category-state,
.doc-by-category-content {
  width: 100%;
}

.doc-by-category-state {
  padding: 8px 16px 32px;
}

.doc-by-category-surface {
  width: min(1100px, 100%);
  margin: 0 auto;
  padding: 10px 0;
  border: 1px solid var(--documents-border);
  border-radius: 22px;
  background: var(--documents-surface);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
}

.doc-by-category-surface--centered {
  padding: 28px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  justify-content: center;
}

.doc-by-category-list {
  display: flex;
  flex-direction: column;
}

.doc-by-category-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  width: 100%;
  padding: 18px 20px;
  border: 0;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.doc-by-category-row:last-child {
  border-bottom: 0;
}

.doc-by-category-row:hover {
  background: var(--documents-hover);
}

.doc-by-category-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: rgba(24, 94, 169, 0.1);
  color: var(--primary-color, #185ea9);
}

.doc-by-category-copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.doc-by-category-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.doc-by-category-name {
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--documents-text);
  line-height: 1.35;
  word-break: break-word;
}

.doc-by-category-date {
  font-size: 0.88rem;
  color: var(--documents-subtle);
}

.doc-by-category-pill,
.doc-by-category-unread {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
}

.doc-by-category-pill {
  background: rgba(24, 94, 169, 0.12);
  color: var(--primary-color, #185ea9);
}

.doc-by-category-unread {
  background: #dc2626;
  color: #ffffff;
}

.doc-by-category-action {
  color: var(--primary-color, #185ea9);
}

.doc-by-category-empty {
  padding: 24px 16px;
}

.doc-by-category-skeleton-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 16px;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(226, 232, 240, 0.9);
}

.doc-by-category-skeleton-row:last-child {
  border-bottom: 0;
}

.doc-by-category-skeleton-copy {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.doc-by-category-module :deep(.p-button) {
  border-radius: 12px;
  font-weight: 600;
}

.doc-by-category-module :deep(.p-button:not(.p-button-text):not(.p-button-link):not(.p-button-secondary)) {
  background: var(--primary-color, #185ea9);
  border-color: var(--primary-color, #185ea9);
}

.doc-by-category-module :deep(.p-button:not(.p-button-text):not(.p-button-link):not(.p-button-secondary):hover) {
  background: var(--primary-dark-color, #0056b3);
  border-color: var(--primary-dark-color, #0056b3);
}

.doc-by-category-module :deep(.p-button.p-button-text) {
  color: var(--primary-color, #185ea9);
}

@media (max-width: 767px) {
  .doc-by-category-state {
    padding: 8px 8px 24px;
  }

  .doc-by-category-surface {
    border-radius: 18px;
  }

  .doc-by-category-row,
  .doc-by-category-skeleton-row {
    gap: 12px;
    padding: 16px 14px;
  }

  .doc-by-category-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
  }

  .doc-by-category-name {
    font-size: 0.96rem;
  }
}
</style>
